import { ReactNode, useEffect, useState } from 'react'

import {
  useExternalArticleSettingsApi,
  useExternalArticleSettingsUpdateApi
} from '@/common/api/modules/externalArticle'
import { useSiteId } from '@/common/app/site'
import { useShowSavedToast } from '@/common/app/toast'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import CheckBox, { CheckBoxType } from '@/components/view/atoms/checkbox/CheckBox'
import FormCaption from '@/components/view/atoms/form/FormCaption'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Heading from '@/components/view/atoms/heading/Heading'
import Textarea from '@/components/view/atoms/textarea/Textarea'
import TextField, { TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import FormRow from '@/components/view/layouts/form/FormRow'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Content from '@/components/view/layouts/page/Content'
import PageListSearching from '@/components/view/molecules/loading/PageListSearching'

// ラベル・入力・説明の1行。既存の設定画面（FormRow + FormLabel + FormCaption）と並びを揃える
const SettingRow = ({
  id,
  label,
  caption,
  children
}: {
  id: string
  label: string
  caption?: ReactNode
  children: ReactNode
}) => (
  <FormRow>
    <FormLabel id={id}>{label}</FormLabel>
    <FormRow direction='vertical' gap={2} size={TextBoxSize.FULL}>
      {children}
      {caption && <FormCaption>{caption}</FormCaption>}
    </FormRow>
  </FormRow>
)

/** 外部記事の取り込み設定。保存すると次の閲覧から新しいパターンが効く。 */
const ExternalArticleSettingsSection = () => {
  const siteId = useSiteId()
  const showSaved = useShowSavedToast({ editing: true })
  const { data, isLoading, mutate } = useExternalArticleSettingsApi({ site_id: siteId })

  const [enabled, setEnabled] = useState(false)
  const [patterns, setPatterns] = useState('')
  const [bodySelector, setBodySelector] = useState('')
  const [excludeMarker, setExcludeMarker] = useState('')
  const [errorText, setErrorText] = useState('')
  const [basicUser, setBasicUser] = useState('')
  // パスワードは値が返らないため、入力されたときだけ送る。
  // 空文字を常に送ると、保存のたびに設定済みのパスワードが消える
  const [basicPass, setBasicPass] = useState<string | null>(null)

  useEffect(() => {
    if (!data) return
    setEnabled(data.enabled)
    setPatterns(data.url_patterns)
    setBodySelector(data.body_selector)
    setExcludeMarker(data.exclude_marker)
    setErrorText(data.error_text)
    setBasicUser(data.basic_user)
    setBasicPass(null)
  }, [data])

  const { trigger: save, isMutating } = useExternalArticleSettingsUpdateApi(() => {
    showSaved('取り込み設定')
    mutate()
  })

  if (isLoading || !data) return <PageListSearching />

  return (
    <Content>
      <Area noBorder>
        <Heading>取り込み設定</Heading>
        <AreaContent>
          <FormCaption size={12}>
            特集ページマスタに載らない記事ページ（ページ管理機能で作った静的ページや、別ドメインのオウンドメディアなど）を
            取り込み、特集ページと併せて
            Media-Driver・記事レコメンド・Findee・AIタグの対象にします。
            記事ページが閲覧された時点で自動的に取り込むため、運営者による登録作業は要りません。
          </FormCaption>

          <SettingRow
            id='external_article_enabled'
            label='外部記事の取り込み'
            caption='有効にすると、対象URLパターンに一致するページの閲覧から自動で取り込みます。'
          >
            {/* 左が ON 側（onChange(true)）。他画面も「ON / OFF」の並びで揃えている */}
            <CheckBox
              type={CheckBoxType.TOGGLE_TEXT}
              name='external_article_enabled'
              checked={enabled}
              toggleText={{ left: '有効', right: '無効' }}
              onChange={setEnabled}
            />
          </SettingRow>

          <SettingRow
            id='url_patterns'
            label='対象URLパターン'
            caption={
              <>
                1行に1つ。`{'{id}'}` を1つ含むパスのテンプレートで書きます（例 /Page/column/c
                {'{id}'}.aspx）。
                <br />
                自社ドメイン以外の記事は https://
                から始まるURLの形で書くと、そのドメインだけに一致し、記事IDに取得元ドメインが付きます（例
                https://note.com/w2/n/{'{id}'}）。
                <br />
                `*` は1セグメント内の任意文字列。先頭が `!`
                の行は除外パターンで、記事URLと同じ形の一覧ページを外すのに使います。
                <br />
                先頭の行が遷移先URLの代表になるため、PC のパターンを先に書いてください。
              </>
            }
          >
            <Textarea
              name='url_patterns'
              value={patterns}
              minRows={4}
              maxLength={2000}
              placeholder={
                '/Page/column/c{id}.aspx\n/SmartPhone/Page/column/c{id}.aspx\n!/Page/column/ccategory*.aspx\nhttps://note.com/w2/n/{id}'
              }
              onChange={(e) => setPatterns(e.target.value)}
            />
          </SettingRow>

          <SettingRow
            id='body_selector'
            label='本文抽出のセレクタ'
            caption='空のときは article / main / body の順に探し、nav・header・footer・script を除きます。'
          >
            <TextField
              name='body_selector'
              size={TextBoxSize.FULL}
              value={bodySelector}
              placeholder='.column-body'
              onChange={(e) => setBodySelector(e.target.value)}
            />
          </SettingRow>

          <SettingRow
            id='exclude_marker'
            label='取込除外マーカー'
            caption='取得したHTMLにこの文字列が含まれる記事は、取り込み対象にしません（レコメンド・分析のどちらにも出ません）。記事を書く担当者が管理画面を触らずHTMLだけで出し分けられます。'
          >
            <TextField
              name='exclude_marker'
              size={TextBoxSize.FULL}
              value={excludeMarker}
              placeholder='<!-- smarthub:no-reco -->'
              onChange={(e) => setExcludeMarker(e.target.value)}
            />
          </SettingRow>

          <SettingRow
            id='error_text'
            label='エラーページ文字列'
            caption='存在しないページでもステータス200でエラーページを返すサイト向け。1日1回の死活確認で、この文字列を含む応答が返る記事を対象から外します。'
          >
            <TextField
              name='error_text'
              size={TextBoxSize.FULL}
              value={errorText}
              placeholder='お探しのページは見つかりませんでした'
              onChange={(e) => setErrorText(e.target.value)}
            />
          </SettingRow>

          <SettingRow
            id='basic_user'
            label='Basic認証'
            caption='記事ページの取得にBasic認証が必要な場合のみ設定します。'
          >
            <ItemAlign gapX={8} align='center'>
              <TextField
                name='basic_user'
                size={TextBoxSize.MEDIUM}
                value={basicUser}
                placeholder='ユーザー名'
                onChange={(e) => setBasicUser(e.target.value)}
              />
              <TextField
                name='basic_pass'
                type='password'
                size={TextBoxSize.MEDIUM}
                value={basicPass ?? ''}
                placeholder={
                  data.basic_pass_set ? '設定済み（変更する場合のみ入力）' : 'パスワード'
                }
                onChange={(e) => setBasicPass(e.target.value)}
              />
              {data.basic_pass_set && basicPass === null && (
                <Button color={ButtonColor.SECONDARY} onClick={() => setBasicPass('')}>
                  パスワードを消す
                </Button>
              )}
            </ItemAlign>
          </SettingRow>
        </AreaContent>
      </Area>

      <ItemAlign>
        <Button
          size='large'
          color={ButtonColor.PRIMARY_BRAND}
          loading={isMutating}
          disabled={isMutating}
          onClick={() =>
            save({
              site_id: siteId,
              enabled,
              url_patterns: patterns,
              body_selector: bodySelector,
              exclude_marker: excludeMarker,
              error_text: errorText,
              basic_user: basicUser,
              ...(basicPass !== null && { basic_pass: basicPass })
            })
          }
        >
          保存する
        </Button>
      </ItemAlign>
    </Content>
  )
}

export default ExternalArticleSettingsSection
